import Link from "next/link";
import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import {
  SmallBodyRegularCSS,
  SmallBodyRegular,
  Heading3,
  Heading4,
  LargeBodyMedium,
  BodyMedium,
  BodyMediumCSS,
} from "@styles/typography";
import { Device, Color } from "@styles/constants";

export const ContentWrap = styled.div`
  padding: 80px 21px 186px 21px;
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  flex-wrap: wrap;

  @media ${Device.mediumUp} {
    flex-wrap: nowrap;
    padding: 40px 21px 20px;
  }
`;

export const ImageWrap = styled.div`
  flex: 1 1 100%;
  max-height: 330px;

  @media ${Device.mediumUp} {
    max-height: 470px;
    flex: 0 0 40%;
  }
`;

export const AffirmWrap = styled.div`
  text-align: left;
  margin-top: 20px;
  p {
    text-align: left;
    margin-bottom: 0;
  }
`;

export const Image = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center top;
`;

export const PaymentTypes = styled.div`
  flex: 0 1 480px;
  margin: 0 auto;

  @media ${Device.mediumUp} {
    flex: 0 0 60%;
    margin: 0;
  }
`;

export const ToggleWrap = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
  padding: 30px 0 0 0;
`;

// apparently framer AnimatePresence wont work if the components share the same name, hence A and B
export const Financing = styled(motion.span)`
  ${BodyMediumCSS}
  margin: 0;
  padding: 0 0 0 15px;
`;

export const DirectPurchase = styled(motion.span)`
  ${BodyMediumCSS}
  margin: 0;
  padding: 0 0 0 15px;
`;

export const Toggle = styled.div<{ isDirectPay: boolean }>`
  width: 72px;
  height: 34px;
  border: 1px solid ${Color.blackBrand};
  border-radius: 60px;
  position: relative;
  cursor: pointer;

  &::after {
    content: "";
    width: 24px;
    height: 24px;
    position: absolute;
    top: 0;
    left: 4px;
    bottom: 0;
    margin: auto 0;
    background-color: ${Color.blackBrand};
    border-radius: 20px;
    transition: transform 0.24s ease;

    ${(props) =>
      props.isDirectPay &&
      css`
        transform: translate3d(38px, 0, 0);
      `}
  }

  @media ${Device.mediumUp} {
    width: 50px;
    height: 24px;

    &::after {
      left: 2px;
      width: 18px;
      height: 18px;

      ${(props) =>
        props.isDirectPay &&
        css`
          transform: translate3d(26px, 0, 0);
        `}
    }
  }
`;

export const DirectPay = styled(motion.div)``;

export const Details = styled.div`
  padding: 0 0 60px;

  p {
    white-space: pre;
  }
`;

export const Copy = styled(SmallBodyRegular)`
  padding: 0;
  margin: 0;

  & + & {
    padding: 10px 0 0;
  }

  svg {
    display: inline-block;
    transform: scale(0.475);
    transform-origin: bottom left;
    height: auto;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: baseline;
  flex-wrap: wrap;
  padding: 60px 0 30px 0;
`;

export const Heading = styled(Heading3)`
  text-align: left;
  margin: 0;
  padding: 0;
  flex: 0 1 auto;
  padding: 0 20px 10px 0;
`;

export const Subheading = styled(Heading4)<{ alignCenter?: boolean }>`
  flex: 0 1 auto;
  margin: 0;
  padding: 0 20px 10px 0;
  text-align: left;

  ${Copy} {
    padding: 10px 0 0;
  }

  ${(props) =>
    props.alignCenter &&
    css`
      position: relative;
      top: -6px;
    `}
`;

export const Price = styled(LargeBodyMedium)`
  margin: 0;

  span {
    color: ${Color.grayInactive};
    padding: 0 0 0 10px;
    text-decoration: line-through;
  }
`;

export const Footer = styled.div`
  padding: 20px 21px;
  display: flex;
  justify-content: stretch;
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px solid ${Color.grayBrand};

  @media ${Device.small} {
    background-color: ${Color.white};
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
  }

  @media ${Device.mediumUp} {
    flex-wrap: wrap;
    min-height: 100px;
  }

  @media ${Device.largeUp} {
    flex-wrap: nowrap;
    padding: 10px 100px;
  }
`;

export const FooterTitle = styled(LargeBodyMedium)`
  flex: 1 1 auto;
  margin: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  padding: 0 20px 0 0;

  @media ${Device.mediumUp} {
    flex: 1 0 100%;
    padding: 0 20px 20px 0;
  }

  @media ${Device.largeUp} {
    padding: 0 20px 0 0;
    flex: 1 1 auto;
    position: relative;
    top: -3px;
  }
`;

export const FooterPrice = styled(BodyMedium)`
  margin: 0;
  position: relative;
  flex: 0 1 auto;

  @media ${Device.mediumUp} {
    padding: 0 34px 0 0;

    &::after {
      content: "";
      width: 1px;
      height: 30px;
      position: absolute;
      top: 0;
      right: 11px;
      bottom: 0;
      margin: auto;
      background-color: ${Color.grayInactive};
    }
  }

  span {
    color: ${Color.grayInactive};
    padding: 0 0 0 10px;
    text-decoration: line-through;
  }
`;

export const FooterInfo = styled.div`
  flex: 1 1 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px solid ${Color.goldAsh};
  padding: 20px 0 0;
  margin: 20px 0 0;

  @media ${Device.mediumUp} {
    flex-wrap: nowrap;
    flex: 1 1 auto;
    border-top: none;
    padding: 0;
    margin: 0;
  }

  @media ${Device.largeUp} {
    flex: 0 1 auto;
  }
`;

export const FooterPriceEst = styled.div`
  ${SmallBodyRegularCSS}
  flex: 1 1 100%;
  text-align: center;
  order: 5;
  padding: 15px 0 0 0;

  @media ${Device.mediumUp} {
    order: initial;
    text-align: left;
    padding: 0 34px 0 0;
    white-space: pre-line;
  }

  p {
    margin-bottom: 0;
  }
`;

export const ShopCta = styled(Link)`
  ${BodyMediumCSS}
  min-height: 50px;
  padding: 0 34px 2px 34px;
  border-radius: 60px;
  background-color: ${Color.blackBrand};
  color: ${Color.white};
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  transition: background-color 0.2s ease;
  flex: 1 1 100%;
  white-space: nowrap;

  &:hover,
  &:focus {
    color: ${Color.white};
    background-color: ${Color.black};
  }
`;
